import React, {
  useEffect, useState,
} from 'react';

import DoneRounded from '@mui/icons-material/DoneRounded';
import RotateRightRounded from '@mui/icons-material/RotateRightRounded';
import ThumbUpRounded from '@mui/icons-material/ThumbUpRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import {
  Badge,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';

import PageContent from '../../components/PageContent';
import Tabs, { TabOption } from '../../components/Tabs';
import { useGetExamAttemptsByUserQuery, useGetExamMarkingRequestsQuery } from '../../services/exams';
import { useGetStaffsQuery } from '../../services/staff';
import {
  ExamAttemptStatus,
  ExamRequestStatus,
  UserGroup,
} from '../../types/common';
import { EXAM_AUTODROP_SWITCH_KEY } from '../../utils/common/constants';
import { joinNames } from '../../utils/common/formatText';
import useTab from '../../utils/react/hooks/useTab';

import AttemptsTab from './AttemptsTab';
import {
  ExamAutoDropForm,
  ExamAutoDropSwitch,
  PageContainer,
} from './index.styles';
import RequestDetails from './RequestDetails';
import RequestsTab from './RequestsTab';

enum Tab {
  PendingApproval = 'pending_approval',
  PendingAcceptance = 'pending_acceptance',
  InProgress = 'in_progress',
  Done = 'done',
}

enum Label {
  PendingApproval = 'Pending Approval',
  PendingAcceptance = 'Pending Acceptance',
  InProgress = 'In Progress',
  Done = 'Done',
}

const ALL_MARKERS = 'ALL';

const TABS: TabOption[] = [
  {
    key: Tab.PendingApproval,
    label: Label.PendingApproval,
    icon: <VisibilityRounded />,
  },
  {
    key: Tab.PendingAcceptance,
    label: Label.PendingAcceptance,
    icon: <ThumbUpRounded />,
  },
  {
    key: Tab.InProgress,
    label: Label.InProgress,
    icon: <RotateRightRounded />,
  },
  {
    key: Tab.Done,
    label: Label.Done,
    icon: <DoneRounded />,
  },
];

const AdminExamAssignmentPage = () => {
  const [isAutoDrop, setAutoDrop] = useState(localStorage.getItem(EXAM_AUTODROP_SWITCH_KEY) === 'true' || false);

  const [tabs, setTabs] = useState<TabOption[]>(TABS);
  const [tab, setTab] = useTab({ default: tabs[0].key });

  const [marker, setMarker] = useState(ALL_MARKERS);
  const [markerOptions, setMarkerOptions] = useState<TabOption[]>([]);

  const [selectedRequestId, setSelectedRequestId] = useState<string | undefined>(undefined);

  const { data: markers, isLoading: isMarkersLoading } = useGetStaffsQuery({
    excludeDisabled: true,
    group: UserGroup.Teachers,
  });

  const { data: examRequests } = useGetExamMarkingRequestsQuery(
    { marker: marker === ALL_MARKERS ? undefined : marker },
  );
  const { data: unmarkedExamAttempts } = useGetExamAttemptsByUserQuery(
    {
      marker: marker === ALL_MARKERS ? undefined : marker,
      marked: false,
    },
  );

  // Generate tab badges
  useEffect(() => {
    const newTabs = [...TABS];

    const pendingApprovalTab = newTabs.find((t) => t.key === Tab.PendingApproval);
    const pendingAcceptanceTab = newTabs.find((t) => t.key === Tab.PendingAcceptance);

    // Pending Approval tab badge
    if (pendingApprovalTab && examRequests) {
      const requests = examRequests.data.filter(
        (r) => r.status === ExamRequestStatus.PendingApproval,
      );
      const count = requests.length;

      pendingApprovalTab.label = (
        <Badge
          badgeContent={count}
          // As requests pending approval requires admin attention, these badges
          // should always be highlighted
          color="success"
        >
          {Label.PendingApproval}
        </Badge>
      );
    }

    // Pending Acceptance tab badge
    if (pendingAcceptanceTab && examRequests) {
      const requests = examRequests.data.filter(
        (r) => [
          ExamRequestStatus.PendingAcceptance,
          ExamRequestStatus.PendingReassignment,
        ].includes(r.status),
      );
      const overdueRequests = requests.filter((r) =>
        (r?.dueDatetime || Infinity) < dayjs().valueOf(),
      );

      pendingAcceptanceTab.label = (
        <Badge
          badgeContent={overdueRequests.length}
          // As requests pending acceptance only requires admin attention when overdue,
          // badges should only contain overdue requests
          color="error"
        >
          {Label.PendingAcceptance}
        </Badge>
      );
    }

    setTabs(newTabs);
  }, [examRequests]);

  useEffect(() => {
    const newTabs = [...TABS];

    const inProgressTab = newTabs.find((t) => t.key === Tab.InProgress);

    // In Progress tab badge
    if (inProgressTab && unmarkedExamAttempts) {
      const overdueAttempts = unmarkedExamAttempts.data.filter((r) =>
        (r?.dueDatetime || Infinity) < dayjs().valueOf(),
      );

      inProgressTab.label = (
        <Badge
          badgeContent={overdueAttempts.length}
          // As requests in progress only requires admin attention when overdue,
          // badges should only contain overdue attempts
          color="error"
        >
          {Label.InProgress}
        </Badge>
      );
    }

    setTabs(newTabs);
  }, [unmarkedExamAttempts]);

  useEffect(() => {
    if (markers) {
      setMarkerOptions([
        { key: ALL_MARKERS, label: 'All' },
        ...markers.data.map((m) => ({
          key: m.staffId,
          label: joinNames([m.firstName, m.lastName]),
        })),
      ]);
    }
  }, [markers]);

  // Close side bar when tab changes
  useEffect(() => {
    setSelectedRequestId(undefined);
  }, [tab]);

  if (isMarkersLoading) {
    return (
      <Skeleton height={180} variant="rectangular" />
    );
  }

  return (
    <PageContent
      sideBarOpen={!!selectedRequestId}
      sideBarWidth={600}
      sideBar={(
        <RequestDetails
          isAutoDrop={isAutoDrop}
          requestId={selectedRequestId}
          onClose={() => setSelectedRequestId(undefined)}
        />
      )}
    >
      <PageContainer>
        <Tabs
          options={TABS}
          selectedKey={tab}
          onChange={(key) => {
            setTab(key);
          }}
        />
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} width="100%">
          <Typography variant="subtitle1" width={80}>Marker</Typography>
          <Tabs
            options={markerOptions}
            selectedKey={marker}
            onChange={(key) => setMarker(key)}
            justifyContent="flex-start"
            dense
          />
          {tab === Tab.PendingApproval && (
            <ExamAutoDropForm
              control={(
                <ExamAutoDropSwitch
                  onChange={(e) => {
                    setAutoDrop(e.target.checked);
                    localStorage.setItem(EXAM_AUTODROP_SWITCH_KEY, e.target.checked.toString());
                  }}
                  checked={localStorage.getItem(EXAM_AUTODROP_SWITCH_KEY) === 'true'}
                />
              )}
              label="Auto Drop"
              labelPlacement="start"
            />
          )}
        </Stack>
        {
          // Render the Tab components separately to each other
          // to keep sorting and filtering logic separate
        }
        {tab === Tab.PendingApproval && (
          <RequestsTab
            marker={marker === ALL_MARKERS ? undefined : marker}
            statuses={[
              ExamRequestStatus.PendingApproval,
            ]}
            onRequestClick={(requestId) => setSelectedRequestId(requestId)}
            selectedRequestId={selectedRequestId}
          />
        )}
        {tab === Tab.PendingAcceptance && (
          <RequestsTab
            marker={marker === ALL_MARKERS ? undefined : marker}
            statuses={[
              ExamRequestStatus.PendingAcceptance,
              ExamRequestStatus.PendingReassignment,
            ]}
            onRequestClick={(requestId) => setSelectedRequestId(requestId)}
            selectedRequestId={selectedRequestId}
          />
        )}
        {tab === Tab.InProgress && (
          <AttemptsTab
            marker={marker === ALL_MARKERS ? undefined : marker}
            status={ExamAttemptStatus.MarkingInProgress}
          />
        )}
        {tab === Tab.Done && (
          <AttemptsTab
            marker={marker === ALL_MARKERS ? undefined : marker}
            status={ExamAttemptStatus.Marked}
          />
        )}
      </PageContainer>
    </PageContent>
  );
};

export default AdminExamAssignmentPage;
